import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-phone-line',
    title: 'Call or Book Online',
    description: 'Get in touch with us by phone or through our website. We\'ll discuss your electrical needs and schedule a convenient time.',
  },
  {
    number: '02',
    icon: 'ri-map-pin-2-line',
    title: 'We Come to You',
    description: 'Our licensed electrician arrives on time, fully equipped with all the tools and parts needed to get the job done right.',
  },
  {
    number: '03',
    icon: 'ri-search-eye-line',
    title: 'Assess & Quote',
    description: 'We inspect the work, provide a clear upfront quote with no hidden fees — so you know exactly what to expect before we start.',
  },
  {
    number: '04',
    icon: 'ri-tools-line',
    title: 'Work Gets Done',
    description: 'Our team completes the job to the highest standard, following all safety regulations and leaving your space clean and tidy.',
  },
  {
    number: '05',
    icon: 'ri-shield-check-line',
    title: 'Quality Check',
    description: 'Every job is tested and inspected before we leave, ensuring everything is safe, compliant, and working perfectly.',
  },
];

export default function OurProcess() {
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>(new Array(steps.length).fill(false));
  const [lineProgress, setLineProgress] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          steps.forEach((_, index) => {
            setTimeout(() => {
              setVisibleSteps(prev => {
                const next = [...prev];
                next[index] = true;
                return next;
              });
              setLineProgress(((index + 1) / steps.length) * 100);
            }, index * 200);
          });
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <div className="inline-block bg-[#f67f19] text-white px-4 py-2 rounded-full font-bold text-xs md:text-sm mb-4 uppercase tracking-wide">
            How It Works
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#535669] mb-4 leading-tight">
            Our Simple Process
          </h2>
          <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
            From your first call to the final check — here's exactly what to expect when you book with us.
          </p>
        </div>

        {/* Desktop Timeline */}
        <div className="hidden md:block relative">
          {/* Flowing connector line */}
          <div className="absolute top-[52px] left-[10%] right-[10%] h-1 bg-gray-100 rounded-full z-0">
            <div
              className="h-full bg-gradient-to-r from-[#f67f19] via-[#ffaa55] to-[#f67f19] rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${lineProgress}%` }}
            />
            {/* Animated glow dot */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-[#f67f19] rounded-full shadow-lg transition-all duration-1000 ease-out"
              style={{
                left: `calc(${lineProgress}% - 8px)`,
                boxShadow: '0 0 12px 4px rgba(246,127,25,0.5)',
                opacity: lineProgress > 0 && lineProgress < 100 ? 1 : 0,
              }}
            />
          </div>

          <div className="grid grid-cols-5 gap-4 relative z-10">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center"
                style={{
                  opacity: visibleSteps[index] ? 1 : 0,
                  transform: visibleSteps[index] ? 'translateY(0)' : 'translateY(32px)',
                  transition: `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`,
                }}
              >
                {/* Icon circle */}
                <div className="relative mb-6">
                  <div
                    className="w-[104px] h-[104px] rounded-full flex items-center justify-center shadow-xl border-4 border-white"
                    style={{
                      background: visibleSteps[index]
                        ? 'linear-gradient(135deg, #f67f19, #ffaa55)'
                        : '#e5e7eb',
                      transition: 'background 0.5s ease',
                    }}
                  >
                    <i className={`${step.icon} text-3xl text-white`}></i>
                  </div>
                  {/* Step number badge */}
                  <div className="absolute -top-1 -right-1 w-7 h-7 bg-[#535669] text-white text-xs font-bold rounded-full flex items-center justify-center shadow">
                    {step.number}
                  </div>
                  {/* Pulse ring */}
                  {visibleSteps[index] && (
                    <div className="absolute inset-0 rounded-full border-2 border-[#f67f19]/40 animate-ping" style={{ animationDuration: '2s' }} />
                  )}
                </div>

                <h3 className="text-base font-bold text-[#535669] mb-2 leading-snug">{step.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed px-1">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Timeline */}
        <div className="md:hidden relative pl-10">
          {/* Vertical line */}
          <div className="absolute left-[18px] top-0 bottom-0 w-0.5 bg-gray-100 rounded-full">
            <div
              className="w-full bg-gradient-to-b from-[#f67f19] to-[#ffaa55] rounded-full transition-all duration-1000 ease-out"
              style={{ height: `${lineProgress}%` }}
            />
          </div>

          <div className="flex flex-col gap-10">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative flex items-start gap-4"
                style={{
                  opacity: visibleSteps[index] ? 1 : 0,
                  transform: visibleSteps[index] ? 'translateX(0)' : 'translateX(-20px)',
                  transition: `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`,
                }}
              >
                {/* Icon on line */}
                <div
                  className="absolute -left-10 top-0 w-9 h-9 rounded-full flex items-center justify-center shadow-md border-2 border-white"
                  style={{
                    background: visibleSteps[index]
                      ? 'linear-gradient(135deg, #f67f19, #ffaa55)'
                      : '#e5e7eb',
                    transition: 'background 0.5s ease',
                  }}
                >
                  <i className={`${step.icon} text-base text-white`}></i>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 flex-1 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-[#f67f19]">{step.number}</span>
                    <h3 className="text-sm font-bold text-[#535669]">{step.title}</h3>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 md:mt-20 text-center">
          <a
            href="tel:0416497716"
            className="inline-flex items-center gap-3 bg-[#f67f19] text-white px-8 py-4 rounded-full font-bold text-base hover:bg-[#e06e0a] transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-fill text-xl"></i>
            Start the Process — Call Now
          </a>
        </div>
      </div>
    </section>
  );
}
